import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { apiClient } from "@/lib/api";
import { Building2, AlertTriangle, Lock } from "lucide-react";

const STATUS_FR = {
  open: ["Ouverte", "bg-zinc-100 text-zinc-700"],
  quoted: ["Devis reçus", "bg-sky-50 text-sky-700"],
  paid: ["Séquestré · en cours", "bg-orange-50 text-[#FF4D00]"],
  in_progress: ["En cours", "bg-orange-50 text-[#FF4D00]"],
  completed: ["Terminée", "bg-emerald-50 text-emerald-700"],
  cancelled: ["Annulée", "bg-red-50 text-red-700"],
};
const eur = (v) => `${Number(v || 0).toLocaleString("fr-FR")} €`;

export default function DonneurOrdreBoard() {
  const [missions, setMissions] = useState(null);
  const [cityFilter, setCityFilter] = useState("");

  useEffect(() => {
    apiClient.get("/missions").then(({ data }) => setMissions(data.missions || [])).catch(() => setMissions([]));
  }, []);

  if (!missions || missions.length < 2) return null;

  const today = new Date().toISOString().slice(0, 10);
  const active = missions.filter((m) => ["paid", "in_progress"].includes(m.status));
  const escrowed = active.reduce((s, m) => s + (m.accepted_amount || 0), 0);
  const late = missions.filter((m) => m.deadline && m.deadline < today && !["completed", "cancelled"].includes(m.status));
  const moFeeOf = (m) => {
    if (m.mo_fee_amount) return m.mo_fee_amount;
    const v = Number(m.mo_fee_value || 0);
    if (!m.mo_fee_type || v <= 0) return 0;
    const base = Number(m.accepted_amount || 0);
    return m.mo_fee_type === "percent" ? (base * v) / 100 : v;
  };
  const feesTotal = missions.reduce((s, m) => s + moFeeOf(m), 0);
  const stats = [
    ["Chantiers", missions.length],
    ["Travaux en cours", active.length],
    ["Terminés", missions.filter((m) => m.status === "completed").length],
    ["Séquestré", eur(escrowed)],
    ["En retard", late.length],
  ];
  if (feesTotal > 0) stats.push(["Mes honoraires", eur(Math.round(feesTotal))]);
  const sorted = [...missions].sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
  const cities = [...new Set(missions.map((m) => m.city).filter(Boolean))].sort();
  const rows = cityFilter ? sorted.filter((m) => m.city === cityFilter) : sorted;

  return (
    <div className="mb-8 border-2 border-[#0A0A0B] bg-white" data-testid="donneur-ordre-board">
      <div className="border-b-2 border-[#0A0A0B] px-6 py-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Building2 className="w-5 h-5 text-[#FF4D00]" />
          <div className="display text-2xl">Vue consolidée de vos chantiers</div>
        </div>
        {cities.length > 1 && (
          <select
            value={cityFilter}
            onChange={(e) => setCityFilter(e.target.value)}
            className="border-2 border-[#0A0A0B] bg-white px-3 py-1.5 text-sm mono uppercase tracking-wider focus:outline-none focus:ring-2 focus:ring-[#FF4D00]"
            data-testid="do-city-filter"
          >
            <option value="">Toutes les villes ({missions.length})</option>
            {cities.map((c) => (
              <option key={c} value={c}>{c} ({missions.filter((m) => m.city === c).length})</option>
            ))}
          </select>
        )}
      </div>
      <div className={`grid grid-cols-2 ${stats.length === 6 ? "sm:grid-cols-6" : "sm:grid-cols-5"} divide-x-2 divide-zinc-100 border-b-2 border-zinc-100`}>
        {stats.map(([l, v]) => (
          <div key={l} className="px-4 py-3">
            <div className="mono text-[10px] uppercase tracking-widest text-zinc-500">{l}</div>
            <div className={`display text-2xl ${l === "En retard" && v > 0 ? "text-red-600" : ""}`}>{v}</div>
          </div>
        ))}
      </div>
      <div className="max-h-96 overflow-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="mono text-[10px] uppercase tracking-widest text-zinc-500 text-left">
              <th className="px-4 py-2">Chantier</th>
              <th className="px-4 py-2">Ville</th>
              <th className="px-4 py-2">Montant</th>
              <th className="px-4 py-2">Échéance</th>
              <th className="px-4 py-2">Statut</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((m) => {
              const [label, cls] = STATUS_FR[m.status] || [m.status, "bg-zinc-100"];
              const isLate = m.deadline && m.deadline < today && !["completed", "cancelled"].includes(m.status);
              return (
                <tr key={m.id} className="border-t border-zinc-100 hover:bg-zinc-50">
                  <td className="px-4 py-2">
                    <Link to={`/missions/${m.id}`} className="font-bold hover:text-[#FF4D00]" data-testid={`do-mission-${m.id}`}>
                      {m.title}
                    </Link>
                    {m.deposit_percent && !m.balance_paid_at && ["paid", "in_progress"].includes(m.status) && (
                      <span className="ml-2 mono text-[9px] uppercase tracking-wider text-[#FF4D00] inline-flex items-center gap-0.5">
                        <Lock className="w-2.5 h-2.5" /> solde à séquestrer
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-2 text-zinc-600">{m.city}</td>
                  <td className="px-4 py-2">{m.accepted_amount ? eur(m.accepted_amount) : `${eur(m.budget_min)}–${eur(m.budget_max)}`}</td>
                  <td className="px-4 py-2">
                    {m.deadline ? (
                      <span className={isLate ? "text-red-600 font-bold inline-flex items-center gap-1" : "text-zinc-600"}>
                        {isLate && <AlertTriangle className="w-3 h-3" />}
                        {m.deadline.split("-").reverse().join("/")}
                      </span>
                    ) : "—"}
                  </td>
                  <td className="px-4 py-2">
                    <span className={`mono text-[10px] uppercase tracking-widest px-2 py-0.5 ${cls}`}>{label}</span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
